
import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import api from "../services/api";

const TaxInformationPage = () => {
    const { id: businessId } = useParams();
    const [employeeTaxInfo, setEmployeeTaxInfo] = useState([]);
    const [employerTaxInfo, setEmployerTaxInfo] = useState(null);
    const [groupedEmployees, setGroupedEmployees] = useState({});
    const [expandedDI, setExpandedDI] = useState({}); // Keeps track of DI expansion for each card


    const [narrowedData, setNarrowedData] = useState({
        withholdingStates: {},
        unemploymentStates: {},
        diNeededStates: {},
        busCounty: {},
        empCounty: {},
    });
    const [taxData, setTaxData] = useState({});
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchTaxInfo = async () => {
            try {
                const response = await api.get(`/tax-info/${businessId}`);
                const data = response.data.taxInfo;

                setEmployeeTaxInfo(data.employees || []);
                setEmployerTaxInfo(data.employer || null);
                setGroupedEmployees(data.groupedEmployees || {});

                // Transform grouped data for narrowed results
                const withholdingStates = {};
                const unemploymentStates = {};
                const diNeededStates = {};
                const busCounty = {};
                const empCounty = {};

                

                Object.entries(data.groupedEmployees).forEach(([state, workLocs]) => {
                    Object.entries(workLocs).forEach(([workLoc, mandates]) => {
                        Object.entries(mandates).forEach(([mandate, taxRequireds]) => {
                            Object.entries(taxRequireds).forEach(([taxRequired, groupedData]) => {
                                if (groupedData.stateWithholding) {
                                    if (!withholdingStates[groupedData.stateWithholding]) {
                                        withholdingStates[groupedData.stateWithholding] = 0;
                                    }
                                    withholdingStates[groupedData.stateWithholding] += groupedData.count;
                                }

                                if (groupedData.stateUnemployment) {
                                    if (!unemploymentStates[groupedData.stateUnemployment]) {
                                        unemploymentStates[groupedData.stateUnemployment] = 0;
                                    }
                                    unemploymentStates[groupedData.stateUnemployment] += groupedData.count;
                                }

                                if (groupedData.diNeeded) {
                                    if (!diNeededStates[groupedData.diNeeded]) {
                                        diNeededStates[groupedData.diNeeded] = 0;
                                    }
                                    diNeededStates[groupedData.diNeeded] += groupedData.count;
                                }

                                if (groupedData.workcountyNeeded) {
                                    if (!busCounty[groupedData.workcountyNeeded]) {
                                        busCounty[groupedData.workcountyNeeded] = 0;
                                    }
                                    busCounty[groupedData.workcountyNeeded] += groupedData.count;
                                }

                                if (groupedData.rescountyNeeded) {
                                    if (!empCounty[groupedData.rescountyNeeded]) {
                                        empCounty[groupedData.rescountyNeeded] = 0;
                                    }
                                    empCounty[groupedData.rescountyNeeded] += groupedData.count;
                                }
                            });
                        });
                    });
                });

                setNarrowedData({ withholdingStates, unemploymentStates, diNeededStates, busCounty, empCounty });
            } catch (error) {
                console.error("Error fetching tax information:", error);
                setError("Failed to fetch tax information.");
            } finally {
                setLoading(false);
            }
        };

        fetchTaxInfo();
    }, [businessId]);

    const handleStateChange = (state, field, value) => {
        setTaxData((prevTaxData) => ({
            ...prevTaxData,
            [state]: {
                ...prevTaxData[state],
                [field]: value,
            },
        }));
    };

    // const handleSubmit = () => {
    //     console.log("Submitting Tax Information:", taxData);
    //     api.post(`/save-tax-info/${businessId}`, taxData)
    //         .then(() => {
    //             alert("Tax information submitted successfully!");
    //         })
    //         .catch((err) => {
    //             console.error("Error submitting tax information:", err);
    //             alert("Failed to submit tax information.");
    //         });
    // };

    const handleSubmit = () => {
        const formattedTaxData = {
            states: Object.entries(taxData).map(([state, data]) => ({
                state,
                withholdingAccountNumber: data.withholdingAccountNumber || null,
                uiaAccountNumber: data.uiaAccountNumber || null,
                unemploymentTaxRate: data.unemploymentTaxRate || null,
                diHandling: data.diHandling || null,
                diContribution: data.diContribution || null,
                employerRate: data.employerRate || null,
                chargesEmployee: data.chargesEmployee || null,
                employeeShare: data.employeeShare || null,
                employerShare: data.employerShare || null,
            })),
        };
    
        api.post(`/tax-info/${businessId}`, formattedTaxData)
            .then(() => {
                alert("Tax information submitted successfully!");
            })
            .catch((err) => {
                console.error("Error submitting tax information:", err);
                alert("Failed to submit tax information.");
            });
    };
    

    if (loading) return <p>Loading tax information...</p>;
    if (error) return <p>{error}</p>;

    return (
        <div style={{ padding: "20px" }}>
            <h2>Tax Information</h2>

            {/* Employer Tax Information */}
            <div>
                <h3>Employer Tax Information</h3>
                {employerTaxInfo ? (
                    <table style={{ borderCollapse: "collapse", width: "100%", marginBottom: "20px" }}>
                        <thead>
                            <tr>
                                <th>Business State</th>
                                <th>Business County</th>
                                <th>Convenience Rule Applies</th>
                                <th>DI Applies</th>
                                <th>Requires Work State Tax</th>
                                <th>Reciprocity States</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td>{employerTaxInfo.business_state}</td>
                                <td>{employerTaxInfo.business_county}</td>
                                <td>{employerTaxInfo.convenience_rule_applies ? "Yes" : "No"}</td>
                                <td>{employerTaxInfo.di_applies ? "Yes" : "No"}</td>
                                <td>{employerTaxInfo.requires_work_state_tax ? "Yes" : "No"}</td>
                                <td>{employerTaxInfo.reciprocity_states || "N/A"}</td>
                            </tr>
                        </tbody>
                    </table>
                ) : (
                    <p>No employer tax information available.</p>
                )}
            </div>

            {/* Employee Tax Information */}
            <div>
                <h3>Employee Tax Information</h3>
                {employeeTaxInfo.length > 0 ? (
                    <table style={{ borderCollapse: "collapse", width: "100%", marginBottom: "20px" }}>
                        <thead>
                            <tr>
                                <th>Employee State</th>
                                <th>Employee County</th>
                                <th>Work Location</th>
                                <th>Employer Mandated</th>
                                <th>DI Applies</th>
                                <th>Requires Work State Tax</th>
                            </tr>
                        </thead>
                        <tbody>
                            {employeeTaxInfo.map((employee, index) => (
                                <tr key={index}>
                                    <td>{employee.employee_state}</td>
                                    <td>{employee.employee_county}</td>
                                    <td>{employee.work_loc}</td>
                                    <td>{employee.employer_mandated ? "Yes" : "No"}</td>
                                    <td>{employee.di_applies ? "Yes" : "No"}</td>
                                    <td>{employee.requires_work_state_tax ? "Yes" : "No"}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                ) : (
                    <p>No employee tax information available.</p>
                )}
            </div>

            {/* Grouped Employee Information */}
            <div>
                <h3>Grouped Employee Information</h3>
                <table style={{ borderCollapse: "collapse", width: "100%", marginBottom: "20px" }}>
                    <thead>
                        <tr>
                            <th>State</th>
                            <th>Work Location</th>
                            <th>Employer Mandated</th>
                            <th>Tax Required</th>
                            <th>Count</th>
                            <th>State Withholding Needed</th>
                            <th>State Unemployment Needed</th>
                            <th>DI Needed</th>
                        </tr>
                    </thead>
                    <tbody>
                        {Object.entries(groupedEmployees).flatMap(([state, workLocs]) =>
                            Object.entries(workLocs).flatMap(([workLoc, mandates]) =>
                                Object.entries(mandates).flatMap(([mandate, taxRequireds]) =>
                                    Object.entries(taxRequireds).map(([taxRequired, data]) => (
                                        <tr key={`${state}-${workLoc}-${mandate}-${taxRequired}`}>
                                            <td>{state}</td>
                                            <td>{workLoc}</td>
                                            <td>{mandate}</td>
                                            <td>{taxRequired}</td>
                                            <td>{data.count}</td>
                                            <td>{data.stateWithholding || "N/A"}</td>
                                            <td>{data.stateUnemployment || "N/A"}</td>
                                            <td>{data.diNeeded || "N/A"}</td>
                                        </tr>
                                    ))
                                )
                            )
                        )}
                    </tbody>
                </table>
            </div>

            {/* State Taxes Section */}
            <div style={{ margin: "3%" }}>
                <h3 style={{ textAlign: "center", backgroundColor: "teal", color: "white", padding: "10px" }}>
                    State Taxes
                </h3>
                <div style={{ display: "flex", flexWrap: "wrap", gap: "20px", justifyContent: "center" }}>
                {Object.keys({
    ...narrowedData.withholdingStates,
    ...narrowedData.unemploymentStates,
    ...narrowedData.diNeededStates,
    ...narrowedData.busCounty,
    ...narrowedData.empCounty,

}).map((state) => (
    <div
        key={state}
        style={{
            width: "300px",
            backgroundColor: "white",
            padding: "15px",
            border: "1px solid #ddd",
            borderRadius: "5px",
            marginBottom: "10px",
        }}
    >
        <h4 style={{ marginBottom: "10px" }}>{state}</h4>

        {/* Withholding Account Number */}
        {narrowedData.withholdingStates[state] && (
            <label>
                Withholding Account Number:
                <input
                    type="text"
                    value={taxData[state]?.withholdingAccountNumber || ""}
                    onChange={(e) =>
                        handleStateChange(state, "withholdingAccountNumber", e.target.value)
                    }
                />
            </label>
        )}

        {/* UIA Account Number */}
        {narrowedData.unemploymentStates[state] && (
            <label>
                UIA Account Number:
                <input
                    type="text"
                    value={taxData[state]?.uiaAccountNumber || ""}
                    onChange={(e) =>
                        handleStateChange(state, "uiaAccountNumber", e.target.value)
                    }
                />
            </label>
        )}

        {/* Business County */}
        {narrowedData.busCounty[state] && (
            <label>
                Business County Tax Account Number:
                <input
                    type="text"
                    value={taxData[state]?.busCountyAccountNumber || ""}
                    onChange={(e) =>
                        handleStateChange(state, "busCountyAccountNumber", e.target.value)
                    }
                />
            </label>
        )}


        {/* UIA Account Number */}
        {narrowedData.empCounty[state] && (
            <label>
                Employee County Tax Account Number:
                <input
                    type="text"
                    value={taxData[state]?.empCountyAccountNumber || ""}
                    onChange={(e) =>
                        handleStateChange(state, "empCountyAccountNumber", e.target.value)
                    }
                />
            </label>
        )}

        {/* Unemployment Tax Rate */}
        {narrowedData.unemploymentStates[state] && (
            <label>
                Unemployment Tax Rate:
                <input
                    type="text"
                    value={taxData[state]?.unemploymentTaxRate || ""}
                    onChange={(e) =>
                        handleStateChange(state, "unemploymentTaxRate", e.target.value)
                    }
                />
            </label>
        )}

        {/* DI Section */}
{narrowedData.diNeededStates[state] && (
    <div>
        <label>
            {state === "NJ" ? (
                <>
                    DI Handling:
                    <select
                        value={taxData[state]?.diHandling || ""}
                        onChange={(e) => {
                            handleStateChange(state, "diHandling", e.target.value);
                            setExpandedDI((prev) => ({
                                ...prev,
                                [state]: e.target.value === "withdraw", // Expand only this card
                            }));
                        }}
                    >
                        <option value="">Select</option>
                        <option value="noWithdraw">
                            Employer does not withdraw for DI insurance
                        </option>
                        <option value="withdraw">
                            Employer withdraws for DI insurance
                        </option>
                    </select>
                    {expandedDI[state] && (
                        <label>
                            Employer Rate (%):
                            <input
                                type="text"
                                value={taxData[state]?.employerRate || ""}
                                onChange={(e) =>
                                    handleStateChange(state, "employerRate", e.target.value)
                                }
                            />
                        </label>
                    )}
                </>
            ) : state === "NY" ? (
                <>
                    Does the business charge employees 0.5% capped at 0.60 cents per week?
                    <select
                        value={taxData[state]?.chargesEmployee || ""}
                        onChange={(e) => handleStateChange(state, "chargesEmployee", e.target.value)}
                    >
                        <option value="">Select</option>
                        <option value="1">Yes</option>
                        <option value="0">No</option>
                    </select>
                </>
            ) : (
                <>
                    DI Contribution:
                    <select
                        value={taxData[state]?.diContribution || ""}
                        onChange={(e) => {
                            handleStateChange(state, "diContribution", e.target.value);
                            setExpandedDI((prev) => ({
                                ...prev,
                                [state]: e.target.value === "contribute", // Expand only this card
                            }));
                        }}
                    >
                        <option value="">Select</option>
                        <option value="noContribute">
                            Employer does not contribute to DI insurance
                        </option>
                        <option value="contribute">
                            Employer contributes to DI insurance
                        </option>
                    </select>
                    {expandedDI[state] && (
                        <>
                        <p>**Set Employee share 100 and Employer share 0 if employer does not contribute**</p>
                            <label>
                                Employee Share (%):
                                <input
                                    type="text"
                                    value={taxData[state]?.employeeShare || ""}
                                    onChange={(e) =>
                                        handleStateChange(state, "employeeShare", e.target.value)
                                    }
                                />
                            </label>
                            <label>
                                Employer Share (%):
                                <input
                                    type="text"
                                    value={taxData[state]?.employerShare || ""}
                                    onChange={(e) =>
                                        handleStateChange(state, "employerShare", e.target.value)
                                    }
                                />
                            </label>
                        </>
                    )}
                </>
            )}
        </label>
    </div>
)}



    </div>
))}


                </div>
                <div style={{ textAlign: "center", marginTop: "20px" }}>
                    <button
                        style={{
                            backgroundColor: "teal",
                            color: "white",
                            padding: "10px 20px",
                            border: "none",
                            borderRadius: "5px",
                            cursor: "pointer",
                        }}
                        onClick={handleSubmit}
                    >
                        Submit Tax Information
                    </button>
                </div>
            </div>
        </div>
    );
};

export default TaxInformationPage;







